import {
  MouseEvent,
  PointerEvent,
  WheelEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

/** Which way the finger travels to commit: back = rightward, open = leftward. */
export type MobileSwipeDirection = 'back' | 'open'

interface UseMobileSwipeOptions {
  enabled: boolean
  direction: MobileSwipeDirection
  onCommit: () => void
  /** Distance that commits without a flick. */
  threshold?: number
}

export interface MobileSwipeHandlers {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void
  onPointerMove: (event: PointerEvent<HTMLElement>) => void
  onPointerUp: (event: PointerEvent<HTMLElement>) => void
  onPointerCancel: () => void
  onClickCapture: (event: MouseEvent<HTMLElement>) => void
  onWheel: (event: WheelEvent<HTMLElement>) => void
}

interface UseMobileSwipeResult {
  handlers: MobileSwipeHandlers
  /** How far the surface follows the finger, in px, always >= 0. */
  offset: number
  isDragging: boolean
}

// Phone-standard feel: a short fast flick commits as well as a long drag.
const HORIZONTAL_LOCK = 12
const VERTICAL_LOCK = 10
const FLICK_MIN_DISTANCE = 12
const FLICK_VELOCITY = 0.3 // px per ms, over the whole gesture
// Trackpad two-finger swipes arrive as horizontal wheel deltas.
const WHEEL_COMMIT = 70
const WHEEL_WINDOW = 220
const WHEEL_COOLDOWN = 700

// Maps, charts and horizontal scrollers keep their own drag, and dragging
// inside a field selects text instead of swiping the page away.
const IGNORE_SELECTOR =
  '[data-mobile-swipe-ignore], input, textarea, select, [contenteditable="true"], [contenteditable=""]'

interface Gesture {
  x: number
  y: number
  startedAt: number
  lock: '' | 'x' | 'y'
}

export function useMobileSwipe({
  enabled,
  direction,
  onCommit,
  threshold = direction === 'back' ? 80 : 70,
}: UseMobileSwipeOptions): UseMobileSwipeResult {
  const gesture = useRef<Gesture | null>(null)
  const suppressClick = useRef(false)
  const wheel = useRef({total: 0, lastAt: 0, committedAt: 0})
  const [offset, setOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const sign = direction === 'back' ? 1 : -1

  const reset = useCallback(() => {
    gesture.current = null
    setIsDragging(false)
    setOffset(0)
  }, [])

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      suppressClick.current = false
      if (!enabled || event.button > 0) return
      if ((event.target as HTMLElement).closest(IGNORE_SELECTOR)) return
      gesture.current = {
        x: event.clientX,
        y: event.clientY,
        startedAt: Date.now(),
        lock: '',
      }
    },
    [enabled],
  )

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const current = gesture.current
      if (!current || current.lock === 'y') return
      const dx = (event.clientX - current.x) * sign
      const dy = event.clientY - current.y
      if (current.lock === '') {
        if (Math.abs(dy) > VERTICAL_LOCK && Math.abs(dy) > Math.abs(dx)) {
          current.lock = 'y'
          return
        }
        if (dx > HORIZONTAL_LOCK && dx > Math.abs(dy)) {
          current.lock = 'x'
          setIsDragging(true)
        } else return
      }
      setOffset(Math.max(0, dx))
    },
    [sign],
  )

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const current = gesture.current
      if (!current) return
      const dx = (event.clientX - current.x) * sign
      const dy = Math.abs(event.clientY - current.y)
      const elapsed = Math.max(1, Date.now() - current.startedAt)
      const isHorizontal = current.lock !== 'y' && dx > dy
      const commits =
        isHorizontal &&
        (dx > threshold ||
          (dx > FLICK_MIN_DISTANCE && dx / elapsed > FLICK_VELOCITY))
      suppressClick.current = current.lock === 'x' || commits
      reset()
      if (commits) onCommit()
    },
    [onCommit, reset, sign, threshold],
  )

  const onClickCapture = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!suppressClick.current) return
    suppressClick.current = false
    event.stopPropagation()
    event.preventDefault()
  }, [])

  const onWheel = useCallback(
    (event: WheelEvent<HTMLElement>) => {
      if (!enabled) return
      if ((event.target as HTMLElement).closest(IGNORE_SELECTOR)) return
      const horizontal = Math.abs(event.deltaX)
      if (horizontal < 2 || horizontal < Math.abs(event.deltaY)) return
      const now = Date.now()
      const state = wheel.current
      if (now - state.committedAt < WHEEL_COOLDOWN) return
      // Trackpad "swipe back" scrolls content left, reported as negative deltaX.
      const travel = -event.deltaX * sign
      state.total =
        (now - state.lastAt > WHEEL_WINDOW ? 0 : state.total) + travel
      state.lastAt = now
      if (state.total > WHEEL_COMMIT) {
        state.total = 0
        state.committedAt = now
        onCommit()
      }
    },
    [enabled, onCommit, sign],
  )

  const handlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: reset,
      onClickCapture,
      onWheel,
    }),
    [onPointerDown, onPointerMove, onPointerUp, reset, onClickCapture, onWheel],
  )

  return {
    handlers,
    offset: enabled ? offset : 0,
    isDragging: enabled && isDragging,
  }
}
